// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { collection, onSnapshot, query, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

const NotificationsContext = createContext(null);

export function NotificationsProvider({ currentUser, bakeryId, children }) {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      return;
    }

    const effectiveBakeryId = bakeryId || currentUser.uid;
    const notificationsRef = collection(db, 'notifications', effectiveBakeryId, 'items');
    const q = query(notificationsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setNotifications(items);
      },
      (error) => {
        console.error('[NotificationsProvider] Error listening to notifications:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser, bakeryId]);

  const markNotificationAsRead = useCallback(
    async (notificationId) => {
      try {
        if (!currentUser || !notificationId) return;
        const effectiveBakeryId = bakeryId || currentUser.uid;
        const notifRef = doc(db, 'notifications', effectiveBakeryId, 'items', notificationId);
        await updateDoc(notifRef, {
          read: true,
          readAt: new Date()
        });
      } catch (error) {
        console.error('[NotificationsProvider] Failed to mark notification as read:', error);
      }
    },
    [currentUser, bakeryId]
  );

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      if (!currentUser || notifications.length === 0) return;
      const effectiveBakeryId = bakeryId || currentUser.uid;
      await Promise.all(
        notifications.map((notification) => {
          const notifRef = doc(
            db,
            'notifications',
            effectiveBakeryId,
            'items',
            notification.id
          );
          return updateDoc(notifRef, {
            read: true,
            readAt: new Date()
          });
        })
      );
    } catch (error) {
      console.error('[NotificationsProvider] Failed to mark all notifications as read:', error);
    }
  }, [currentUser, bakeryId, notifications]);

  const hideNotification = useCallback(
    async (notificationId) => {
      try {
        if (!currentUser || !notificationId) return;
        const effectiveBakeryId = bakeryId || currentUser.uid;
        const notifRef = doc(db, 'notifications', effectiveBakeryId, 'items', notificationId);
        await updateDoc(notifRef, {
          hidden: true,
          hiddenAt: new Date(),
          hiddenBy: 'baker'
        });
      } catch (error) {
        console.error('[NotificationsProvider] Failed to hide notification:', error);
      }
    },
    [currentUser, bakeryId]
  );

  // Only expose non-hidden notifications to the UI
  const visibleNotifications = notifications.filter((n) => !n.hidden);
  const unreadNotifications = visibleNotifications.filter((n) => !n.read);

  const value = {
    notifications: visibleNotifications, // read + unread, but not hidden
    unreadNotifications,
    unreadCount: unreadNotifications.length,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    hideNotification
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return ctx;
}
